/**
 * Mixpanel Provider
 *
 * Mixpanel SDK 초기화 및 AnalyticsTracker 구현체
 */
import type { AnalyticsTracker } from '../AnalyticsTracker';

// ============================================================================
// Configuration
// ============================================================================

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_PROJECT_TOKEN;
const isMixpanelEnabled = !!MIXPANEL_TOKEN;

// Lazy-loaded Mixpanel instance (bundle-defer-third-party optimization)
let mixpanelModule: typeof import('mixpanel-browser') | null = null;

async function loadMixpanel() {
  if (!mixpanelModule) {
    mixpanelModule = await import('mixpanel-browser');
  }
  return mixpanelModule.default;
}

// ============================================================================
// Super Properties Getter (for debugging)
// ============================================================================

/**
 * 현재 등록된 Super Properties 반환 (디버깅용)
 */
export async function getSuperProperties(): Promise<Record<string, unknown>> {
  if (!isMixpanelEnabled || typeof window === 'undefined') {
    return {};
  }
  try {
    const mixpanel = await loadMixpanel();
    return mixpanel.get_property('$super_properties') || {};
  } catch {
    return {};
  }
}

// ============================================================================
// Super Properties Helpers
// ============================================================================

/**
 * 탭 세션 ID 생성 (탭 열 때마다 새로 생성)
 */
function generateTabSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * User Agent 기반 디바이스 타입 감지
 */
function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const ua = navigator.userAgent.toLowerCase();
  if (/ipad|tablet|playbook|silk|(android(?!.*mobile))/.test(ua)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

/**
 * 정적 Super Properties 등록 (초기화 시 1회)
 * - user_agent: 브라우저 정보
 * - tab_session_id: 탭 세션 식별자
 */
async function registerStaticSuperProperties(): Promise<void> {
  if (!isMixpanelEnabled || typeof window === 'undefined') {
    return;
  }

  const mixpanel = await loadMixpanel();
  const staticProps = {
    user_agent: navigator.userAgent,
    tab_session_id: generateTabSessionId(),
    device_type: getDeviceType(),
  };

  mixpanel.register(staticProps);
}

/**
 * 동적 Super Properties 업데이트 (라우트 변경 시)
 * - page_path: 현재 경로
 * - page_url: 현재 전체 URL
 * - previous_page_path: 이전 경로
 * - previous_page_url: 이전 전체 URL
 */
export async function updatePageSuperProperties(
  currentPath: string,
  previousPath?: string,
): Promise<void> {
  if (!isMixpanelEnabled || typeof window === 'undefined') {
    return;
  }

  const mixpanel = await loadMixpanel();
  const currentUrl = window.location.href;
  const previousUrl = previousPath ? `${window.location.origin}${previousPath}` : undefined;

  const dynamicProps = {
    page_path: currentPath,
    page_url: currentUrl,
    ...(previousPath && { previous_page_path: previousPath }),
    ...(previousUrl && { previous_page_url: previousUrl }),
  };

  mixpanel.register(dynamicProps);
}

// ============================================================================
// Initialization
// ============================================================================

// Mixpanel SDK 초기화 완료 Promise (lazy)
let _mixpanelReadyPromise: Promise<void> | null = null;

export const mixpanelReady: Promise<void> = new Promise<void>(resolve => {
  if (!isMixpanelEnabled) {
    resolve();
    return;
  }

  _mixpanelReadyPromise = (async () => {
    const mixpanel = await loadMixpanel();
    await new Promise<void>(innerResolve => {
      mixpanel.init(MIXPANEL_TOKEN!, {
        debug: process.env.NODE_ENV === 'development',
        track_pageview: false,
        persistence: 'localStorage',
        opt_out_tracking_by_default: !isMixpanelEnabled,
        loaded: () => {
          registerStaticSuperProperties();
          innerResolve();
        },
      });
    });
    resolve();
  })();
});

// Production에서 토큰 미설정 시 경고는 서버 로그에서만 확인

// ============================================================================
// MixpanelProvider (AnalyticsTracker 구현체)
// ============================================================================

export class MixpanelProvider implements AnalyticsTracker {
  async track(eventName: string, properties?: Record<string, unknown>): Promise<void> {
    if (!isMixpanelEnabled) {
      return;
    }
    await mixpanelReady;
    const mixpanel = await loadMixpanel();
    mixpanel.track(eventName, properties as Record<string, unknown>);
  }

  async identify(userId: string): Promise<void> {
    if (!isMixpanelEnabled) {
      return;
    }
    await mixpanelReady;
    const mixpanel = await loadMixpanel();
    mixpanel.identify(userId);
  }

  async getDistinctId(): Promise<string | null> {
    if (!isMixpanelEnabled) {
      return null;
    }
    await mixpanelReady;
    try {
      const mixpanel = await loadMixpanel();
      return mixpanel.get_distinct_id();
    } catch {
      return null;
    }
  }

  async setUserProperties(properties: Record<string, unknown>): Promise<void> {
    if (!isMixpanelEnabled) {
      return;
    }
    await mixpanelReady;
    const mixpanel = await loadMixpanel();
    mixpanel.people.set(properties);
  }

  setUser(_userId: string, properties: Record<string, unknown>): void {
    this.setUserProperties(properties);
  }

  clearUser(): void {
    // Mixpanel은 세션 간 사용자 유지, reset 불필요
  }
}
